import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

const SUSPENSION_POLL_MS = 5 * 60 * 1000;   // re-check a live session every 5 min

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suspended, setSuspended] = useState(false);

  // Suspension bans the user in Supabase Auth, which blocks new sign-ins — but an
  // already-open session keeps working until its token expires. This ends it promptly.
  const checkSuspension = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('subscription_status, suspended_at')
        .eq('id', userId)
        .maybeSingle();

      // A failed query must never sign anyone out — offline is not suspended.
      if (error || !data) return;

      if (data.subscription_status === 'suspended' || data.suspended_at) {
        setSuspended(true);
        window.alert('Your account has been suspended. Please contact support.');
        await supabase.auth.signOut();
        setUser(null);
      }
    } catch {
      /* never let this break the app */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const settle = (session) => {
      if (cancelled) return;
      setUser(session?.user ?? null);
      setLoading(false);
      // Fire and forget — never awaited, to avoid blocking auth initialisation.
      if (session?.user) setTimeout(() => checkSuspension(session.user.id), 0);
    };

    // Session handoff from the landing page.
    //
    // Supabase stores its session in localStorage, which is scoped to the ORIGIN —
    // a session created on carcollectorstudio.com is NOT visible here on
    // app.carcollectorstudio.com. The landing page therefore passes the tokens in
    // the URL fragment (never sent to a server, never logged), and we adopt them.
    //
    // Done explicitly rather than relying on supabase-js `detectSessionInUrl`,
    // whose behaviour differs between the implicit and PKCE flows.
    const hash = window.location.hash || '';
    const hashParams = hash.includes('access_token=') ? new URLSearchParams(hash.slice(1)) : null;
    const handoffAccess  = hashParams?.get('access_token');
    const handoffRefresh = hashParams?.get('refresh_token');

    if (handoffAccess && handoffRefresh) {
      // Strip the tokens from the address bar straight away.
      window.history.replaceState({}, document.title, window.location.pathname);
      supabase.auth.setSession({ access_token: handoffAccess, refresh_token: handoffRefresh })
        .then(({ data, error }) => {
          if (error) console.error('[auth] session handoff failed', error);
          settle(data?.session ?? null);
        })
        .catch((err) => { console.error('[auth] session handoff threw', err); settle(null); });
    } else {
      supabase.auth.getSession()
        .then(({ data: { session } }) => settle(session))
        .catch((err) => { console.error('[auth] getSession failed', err); settle(null); });
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // ⚠️ This callback must stay SYNCHRONOUS. Supabase holds an internal lock while
      // it runs; awaiting another supabase call from inside it deadlocks the client
      // and every subsequent query hangs forever (symptom: an endless spinner, no error).
      // Anything async must be deferred with setTimeout so it runs after the lock clears.
      const u = session?.user ?? null;
      setUser(u);
      setLoading(false);
      if (event === 'SIGNED_OUT') setSuspended(false);
      if (u && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        setTimeout(() => checkSuspension(u.id), 0);
      }
    });

    return () => { cancelled = true; subscription.unsubscribe(); };
  }, [checkSuspension]);

  // Poll while the tab is visible so a suspension takes effect within minutes.
  useEffect(() => {
    if (!user?.id) return;
    const tick = () => { if (document.visibilityState === 'visible') checkSuspension(user.id); };
    const timer = setInterval(tick, SUSPENSION_POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', tick); };
  }, [user?.id, checkSuspension]);

  return (
    <AuthContext.Provider value={{ user, loading, suspended }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);